import { SearchParagraphWord, TranscriptSelectionRange, TranscriptSelectionRanges } from '../types';
import { Word } from '@/domains/transcript';
import { Clip, ClipDeletes } from '@/domains/asset';
import {
  DeletedRange,
  EditedWord,
  saveClipChanges,
  updateClipCaptionsLoadingState,
  updateClipWithId
} from '@/stores/clip';
import { markCaptionsObsolete } from '@/stores/captions';
import { addDecimalIfNeeded } from '@/libs/utils';
import { getEditsForSegment, getMergesForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';

/**
 *
 * @param selectionRanges ranges of words selected in transcript
 * @returns new deletes that are inside active words selected ranges
 */
export function getDeletesFromSelectedRanges(selectionRanges: TranscriptSelectionRanges | null): ClipDeletes {
  return selectionRanges
    ? selectionRanges?.active.reduce((acc: ClipDeletes, el: TranscriptSelectionRange) => {
        return {
          ...acc,
          [el.start]: {
            prev_end_time: el.start,
            next_start_time: el.end,
            bounds: [el.start, el.end]
          }
        };
      }, {})
    : {};
}

/**
 *
 * @param words that we need boundaries for
 * @returns first and last words on the words array
 */
export function getWordsBoundaries(words: Word[]): { firstWord: Word; lastWord: Word } {
  return {
    firstWord: words[0],
    lastWord: words[words.length - 1]
  };
}

/**
 *
 * @param firstSelectedWord from transcript selection
 * @param lastSelectedWord from transcript selection
 * @param clipDeletes from editing clip
 * @returns restoring & keepeing deletes based on selection. In case we select words from
 * multiple different deletes, all deletes would be part of restoring object
 */
export function getSelectionPartitionedDeletes(
  firstSelectedWord: Word,
  lastSelectedWord: Word,
  clipDeletes: ClipDeletes
): {
  restoring: ClipDeletes;
  keeping: ClipDeletes;
} {
  return Object.values(clipDeletes).reduce(
    (acc: { restoring: ClipDeletes; keeping: ClipDeletes }, deleteItem: DeletedRange) => {
      if (lastSelectedWord.end_time >= deleteItem.bounds[0] && firstSelectedWord.start_time <= deleteItem.bounds[1]) {
        acc.restoring[deleteItem.bounds[0]] = deleteItem;
      } else {
        acc.keeping[deleteItem.bounds[0]] = deleteItem;
      }
      return acc;
    },
    { restoring: {}, keeping: {} }
  );
}

/**
 * Merges entries in an object where the `bounds[1]` of one entry matches the `bounds[0]` of another.
 *
 * The function processes the `deletes` object by iterating through its entries,
 * merging consecutive entries that have matching bounds, and returns a new object with merged entries.
 *
 * @param {ClipDeletes} deletes - Deletes of a clip
 *
 * @returns {ClipDeletes} - A new object with merged entries. Keys are based on the `bounds[0]` values of the resulting entries.
 *
 * @example
 * const deletes = {
 *   "0.24": {
 *     "bounds": [0.24, 0.56],
 *     "prev_end_time": 0.24,
 *     "next_start_time": 0.56
 *   },
 *   "0.56": {
 *     "bounds": [0.56, 4],
 *     "prev_end_time": 0.56,
 *     "next_start_time": 4
 *   }
 * };
 *
 * const result = mergeDeletes(deletes);
 * // Output:
 * // {
 * //   "0.24": {
 * //     "bounds": [0.24, 4],
 * //     "prev_end_time": 0.24,
 * //     "next_start_time": 4
 * //   }
 * // }
 */
export function mergeDeletes(deletes: ClipDeletes): ClipDeletes {
  const deletesValues = Object.values(deletes);

  // Sort the entries by their bounds[0] to process them in order
  deletesValues.sort((a, b) => a.bounds[0] - b.bounds[0]);

  const merged: DeletedRange[] = [];

  for (let i = 0; i < deletesValues.length; i++) {
    const current = { ...deletesValues[i] };
    let j = i + 1;

    while (j < deletesValues.length && current.bounds[1] >= deletesValues[j].bounds[0]) {
      // Don't merge if one delete has deleteType as FILLER_WORDS_MENU and the other doesn't
      const currentIsFillerWordsMenu = current.deleteType === 'FILLER_WORDS_MENU';
      const nextIsFillerWordsMenu = deletesValues[j].deleteType === 'FILLER_WORDS_MENU';

      if (currentIsFillerWordsMenu !== nextIsFillerWordsMenu) {
        // One is FILLER_WORDS_MENU and the other isn't, so don't merge them
        break;
      }

      current.bounds = [current.bounds[0], Math.max(current.bounds[1], deletesValues[j].bounds[1])];
      current.next_start_time = deletesValues[j].next_start_time;
      j++;
    }

    if (current.bounds[0] !== current.bounds[1]) {
      merged.push(current);
    }
    i = j - 1;
  }

  return merged.reduce((result, entry) => {
    result[entry.bounds[0]] = entry;
    return result;
  }, {});
}

export function restoreDelete(deletes: ClipDeletes, restoreRange: DeletedRange) {
  // Find and remove deletes that overlap with the selected time range
  const newDeletes = structuredClone(deletes);
  const restoreDeleteStartTime = restoreRange.bounds[0];
  const restoreDeleteEndTime = restoreRange.bounds[1];

  const keysToRemove: string[] = [];
  const keysToUpdate: { [key: string]: any } = {};

  Object.entries(newDeletes).forEach(([key, deleteItem]) => {
    const deleteStart = deleteItem.bounds[0];
    const deleteEnd = deleteItem.bounds[1];

    // Check if the selected time range overlaps with this delete range
    const hasOverlap =
      (restoreDeleteStartTime >= deleteStart && restoreDeleteStartTime < deleteEnd) ||
      (restoreDeleteEndTime > deleteStart && restoreDeleteEndTime <= deleteEnd) ||
      (restoreDeleteStartTime <= deleteStart && restoreDeleteEndTime >= deleteEnd);

    if (hasOverlap) {
      // Check if the selection completely encloses the delete range
      const isCompletelyEnclosed = restoreDeleteStartTime <= deleteStart && restoreDeleteEndTime >= deleteEnd;

      if (isCompletelyEnclosed) {
        // Completely remove the delete
        keysToRemove.push(key);
      } else {
        // Check if the selection is completely within the delete range
        const isSelectionWithinDelete = restoreDeleteStartTime >= deleteStart && restoreDeleteEndTime <= deleteEnd;

        if (isSelectionWithinDelete) {
          // Split the delete into two parts
          const firstDeleteStart = deleteStart;
          const firstDeleteEnd = restoreDeleteStartTime;
          const secondDeleteStart = restoreDeleteEndTime;
          const secondDeleteEnd = deleteEnd;

          // Remove the original delete
          keysToRemove.push(key);

          // Add the first part if it's valid
          if (firstDeleteStart < firstDeleteEnd) {
            const firstDeleteKey = firstDeleteStart.toString();
            keysToUpdate[firstDeleteKey] = {
              bounds: [firstDeleteStart, firstDeleteEnd],
              prev_end_time: firstDeleteStart,
              next_start_time: firstDeleteEnd
            };
          }

          // Add the second part if it's valid
          if (secondDeleteStart < secondDeleteEnd) {
            const secondDeleteKey = secondDeleteStart.toString();
            keysToUpdate[secondDeleteKey] = {
              bounds: [secondDeleteStart, secondDeleteEnd],
              prev_end_time: secondDeleteStart,
              next_start_time: secondDeleteEnd
            };
          }
        } else {
          // Partially overlap - update the delete bounds
          let newDeleteStart = deleteStart;
          let newDeleteEnd = deleteEnd;

          // If selection starts within the delete range, adjust the end
          if (restoreDeleteStartTime > deleteStart && restoreDeleteStartTime < deleteEnd) {
            newDeleteEnd = restoreDeleteStartTime;
          }

          // If selection ends within the delete range, adjust the start
          if (restoreDeleteEndTime > deleteStart && restoreDeleteEndTime < deleteEnd) {
            newDeleteStart = restoreDeleteEndTime;
          }

          // Only update if the new bounds are valid (start < end)
          if (newDeleteStart < newDeleteEnd) {
            keysToUpdate[key] = {
              ...deleteItem,
              bounds: [newDeleteStart, newDeleteEnd],
              prev_end_time: newDeleteStart,
              next_start_time: newDeleteEnd
            };
          } else {
            // If the bounds are invalid, remove the delete entirely
            keysToRemove.push(key);
          }
        }
      }
    }
  });

  // Remove the completely enclosed deletes
  keysToRemove.forEach(key => {
    delete newDeletes[key];
  });

  // Update the partially overlapped deletes
  Object.entries(keysToUpdate).forEach(([key, updatedDelete]) => {
    newDeletes[key] = updatedDelete;
  });

  return mergeDeletes(newDeletes);
}

/**
 * Updates the clip's word edits with a new word value for a specific word's start time.
 * In case of hide - value is sent as an empty string
 *
 * @param {Word} word - The word to be edited
 * @param {string} value - The new word content. In case of hide - value is sent as an empty string
 * @param {Clip} values - The clip to update
 * @returns {Clip} - A new clip object with the updated word edits in `asset_metadata.edits`.
 *
 * @example
 * const word = { start_time: "0.5", end_time: "1.0" };
 * const clip = { asset_metadata: { edits: {} } };
 * const updatedClip = editClipWord(word, "newValue", clip);
 * console.log(updatedClip.asset_metadata.edits["0.5"]);
 * // Output: { start_time: "0.5", end_time: "1.0", word: "newValue" }
 */
export function editClipWord(
  word: Word | SearchParagraphWord,
  value: string,
  clipData: Clip,
  addToMerges: boolean = false
): Clip {
  const editedWord: EditedWord = {
    start_time: word.start_time,
    end_time: word.end_time,
    word: value
  };
  const currentWordEdit = getEditsForSegment(clipData)?.[addDecimalIfNeeded(word.start_time)];
  const isHiddenWord = currentWordEdit && !currentWordEdit.word;
  return {
    ...clipData,
    asset_metadata: {
      ...clipData.asset_metadata,
      edits: {
        ...clipData.asset_metadata?.edits,
        // we identify hidden by `word: ''`, and if we override edits, it would make it non-deleted
        ...(isHiddenWord
          ? {}
          : {
              [addDecimalIfNeeded(word.start_time)]: {
                start_time: word.start_time,
                end_time: word.end_time,
                word: value
              }
            })
      },
      // Add to merges to cover the case edit -> hide. Without this it would revert to the original content
      merges: addToMerges
        ? (getMergesForSegment(clipData) ?? [])
            .filter(({ start_time }) => start_time !== editedWord.start_time)
            .concat(editedWord)
        : (clipData.asset_metadata.merges ?? [])
    }
  };
}

/**
 * Merges a word into a clip's metadata, updating the "merges" array and "edits" object.
 *
 * This function adds an `EditedWord` to the "merges" array in the clip's metadata,
 * when correcting transcript with multiple selected words
 *
 * @param {string} word - New corrected value
 * @param {Clip} clipData - The existing clip data to be updated.
 * @param {number} startTime - The start time correcting words
 * @param {number} endTime - The end time of correcting words
 * @returns {Clip} - A new `Clip` object with updated metadata.
 *
 * @example
 * const updatedClip = mergeClipWords("new word content", clipData, 1, 5);
 * console.log(updatedClip);
 * // Output:
 * // {
 * //   asset_metadata: {
 * //     merges: [
 * //       { start_time: 1, end_time: 5, word: "new word content" }
 * //     ],
 * //     edits: {
 * //       1: { start_time: 1, end_time: 5, word: "new word content" },
 * //     }
 * //   }
 * // }
 */
export function mergeClipWords(word: string, clipData: Clip, startTime: number, endTime: number): Clip {
  const editedWord: EditedWord = {
    start_time: startTime,
    end_time: endTime,
    word
  };
  return {
    ...clipData,
    asset_metadata: {
      ...clipData.asset_metadata,
      merges: (clipData.asset_metadata.merges ?? [])
        .filter(({ start_time }) => start_time !== editedWord.start_time)
        .filter(({ start_time }) => !(start_time >= editedWord.start_time && start_time <= editedWord.end_time))
        .concat(editedWord),
      edits: { ...clipData.asset_metadata.edits, [editedWord.start_time]: editedWord }
    }
  };
}

export function saveCorrectWordChanges(updatedClipValue: Clip, clipData: Clip) {
  markCaptionsObsolete({ clipId: clipData.id });
  updateClipCaptionsLoadingState({ clipId: clipData.id, loading: true });
  updateClipWithId(clipData.id, updatedClipValue, true);
  saveClipChanges({
    clipId: clipData.id,
    shouldGenerateThumbnail: !!updatedClipValue,
    shouldAddToHistory: true,
    isEditedState: clipData.asset_metadata.is_edited
  });
}

/**
 * Merges overlapping or touching ranges into non-overlapping ranges.
 *
 * The returned ranges are sorted by start.
 *
 * @param ranges - An array of [start, end] ranges. Each range must have start < end.
 * @returns A new array of non-overlapping, merged ranges sorted by start.
 *
 * @example
 * mergeWindows([[1, 3], [2, 5], [6, 8]]);
 * // returns [[1, 5], [6, 8]]
 */
export const mergeWindows = (ranges: [number, number][]) => {
  if (ranges.length === 0) {
    return [];
  }
  const sortedRange = ranges.slice().sort((a, b) => (a[0] === b[0] ? b[1] - a[1] : a[0] - b[0]));
  const mergedRanges: [number, number][] = [];
  let currentRange = sortedRange[0];
  for (let i = 1; i < sortedRange.length; i++) {
    const [currStart, currEnd] = currentRange;
    const [nextStart, nextEnd] = sortedRange[i];
    if (currEnd >= nextStart) {
      currentRange = [currStart, Math.max(currEnd, nextEnd)];
    } else {
      mergedRanges.push(currentRange);
      currentRange = [...sortedRange[i]];
    }
  }
  mergedRanges.push(currentRange);
  return mergedRanges;
};

/**
 * Subtracts a list of non-overlapping, sorted ranges from a single target range.
 *
 * @param targetWindow - The range to subtract from, e.g., [5, 10]. Must have start < end.
 * @param windowsToSubtract - Ranges to remove from the target. Must be non-overlapping and sorted by start.
 * @returns An array of ranges representing the portions of the target that remain after subtraction.
 *
 * @example
 * subtractRange([5, 10], [[7, 9]]);
 * // returns [[5, 7], [9, 10]]
 */
const subtractRange = (targetWindow: [number, number], windowsToSubtract: [number, number][]): [number, number][] => {
  const [targetStart, targetEnd] = targetWindow;

  const out: [number, number][] = [];
  let cur = targetStart;

  for (const [deleteStart, deleteEnd] of windowsToSubtract) {
    if (deleteEnd <= cur) {
      // ignore delete before target start
      continue;
    }
    if (deleteStart >= targetEnd) {
      // stop computation for deletes after target end
      break;
    }
    if (deleteStart > cur) {
      // delete caused split, add the gap
      out.push([cur, Math.min(deleteStart, targetEnd)]);
    }
    cur = Math.max(cur, deleteEnd);
    if (cur >= targetEnd) {
      // full portion deleted
      return out;
    }
  }

  if (cur < targetEnd) {
    // remaining portion of target
    out.push([cur, targetEnd]);
  }
  return out;
};

/**
 * Subtracts a list of ranges from another list of target ranges.
 *
 * Each range in `toRemove` is merged first to ensure there are no overlaps and then subtracted
 * from each target range in `ranges`.
 *
 * @param ranges - Array of target ranges, e.g., [[5, 10], [15, 20]].
 * @param toRemove - Array of ranges to subtract, e.g., [[7, 9], [18, 19]].
 * @returns Array of ranges representing the portions of `ranges` remaining after subtraction.
 *
 * @example
 * subtractRanges([[5, 10], [15, 20]], [[7, 9], [18, 19]]);
 * // returns [[5, 7], [9, 10], [15, 18], [19, 20]]
 */
export const subtractRanges = (ranges: [number, number][], toRemove: [number, number][]): [number, number][] => {
  const answer: [number, number][] = [];
  const mergedAndSortedRanges = mergeWindows(ranges);
  const mergedAndSortedToRemove = mergeWindows(toRemove);
  for (const range of mergedAndSortedRanges) {
    answer.push(...subtractRange(range, mergedAndSortedToRemove));
  }
  return answer;
};
